import type { Point2D } from '@utils/geometry'

interface OBB {
  center: Point2D
  width: number
  height: number
  rotation: number // in radians
}

function subtractVector(a: Point2D, b: Point2D): Point2D {
  return { x: a.x - b.x, y: a.y - b.y }
}

function dotProduct(a: Point2D, b: Point2D) {
  return a.x * b.x + a.y * b.y
}

/*
 * the cross product of two 2D vectors shows the rotation / orientation the two
 * vectors make with each other. A positive cross product indicates a counter-clockwise turn,
 * negative indicates a clockwise turn, and zero indicates collinear points.
*/
function crossProduct(o: Point2D, a: Point2D, b: Point2D): number {
  return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x)
}

/**
 * Computes the Convex Hull using the Monotone Chain algorithm.
 * Time Complexity: O(n log n) due to sorting.
 */
function getConvexHull(points: Point2D[]): Point2D[] {
  const n = points.length
  if (n <= 2) return points

  // Sort by x coordinate (and y if x is equal)
  const sorted = [...points].sort((a, b) => a.x === b.x ? a.y - b.y : a.x - b.x)

  // Build lower hull, we're going from left to right
  // the loop basically finds a hull for the lower and the left part of the points
  const lower: Point2D[] = []
  for (const p of sorted) {
    while (lower.length >= 2 && crossProduct(lower[lower.length - 2]!, lower[lower.length - 1]!, p) <= 0) {
      lower.pop()
    }
    lower.push(p)
  }

  // Build upper hull, we're going from right to left (the array is sorted in ascending order)
  // the loop basically finds a hull for the upper and the right part of the points
  const upper: Point2D[] = []
  for (const p of [...sorted].reverse()) {
    while (upper.length >= 2 && crossProduct(upper[upper.length - 2]!, upper[upper.length - 1]!, p) <= 0) {
      upper.pop()
    }
    upper.push(p)
  }

  // Concatenate (remove duplicate start/end points)
  upper.pop()
  lower.pop()
  return lower.concat(upper)
}

/**
 * Computes the Minimum Bounding Box (oriented) for a set of 2D points.
 * utilizing the Rotating Calipers method.
 */
function getMinimumBoundingBox(points: Point2D[]): OBB | undefined {
  // Compute the convex hull of all points
  // This reduces the problem space to only the outermost points
  const hull = getConvexHull(points)

  // Initialize tracking variables for the smallest bounding box
  let minArea = Infinity
  let bestOBB: OBB | undefined = undefined

  // Iterate over all edges of the convex hull
  // For each edge, we'll compute a bounding box aligned with that edge
  for (let i = 0; i < hull.length; i++) {
    const p1 = hull[i]!
    const p2 = hull[(i + 1) % hull.length]! // Wrap to start

    // Calculate the edge vector and its length
    const edge = subtractVector(p2, p1)
    const edgeLen = Math.sqrt(edge.x * edge.x + edge.y * edge.y)

    // Skip degenerate edges (zero length)
    if (edgeLen === 0) continue

    // Create a local coordinate system aligned with this edge
    // u-axis: unit vector along the edge direction
    // v-axis: unit vector perpendicular to the edge (90° counterclockwise rotation)
    const u = { x: edge.x / edgeLen, y: edge.y / edgeLen }
    const v = { x: -u.y, y: u.x }

    // Project all hull points onto the u and v axes
    // This gives us the bounding box dimensions in the local coordinate system
    let minU = Infinity, maxU = -Infinity
    let minV = Infinity, maxV = -Infinity

    // find the min and max projections along u and v axes
    // which will define the bounding box in this orientation
    for (const hullPoint of hull) {
      const projectedU = dotProduct(hullPoint, u)
      const projectedV = dotProduct(hullPoint, v)

      if (projectedU < minU) minU = projectedU
      if (projectedU > maxU) maxU = projectedU
      if (projectedV < minV) minV = projectedV
      if (projectedV > maxV) maxV = projectedV
    }

    // Calculate the area of this oriented bounding box
    const width = maxU - minU
    const height = maxV - minV
    const area = width * height

    // Check if this is the smallest bounding box found so far
    if (area < minArea) {
      minArea = area

      //Compute the center point of the bounding box in local (u, v) space
      const centerU = (minU + maxU) / 2
      const centerV = (minV + maxV) / 2

      // Transform the center back to global (x, y) coordinates
      const centerX = centerU * u.x + centerV * v.x
      const centerY = centerU * u.y + centerV * v.y

      // Store the best bounding box with all its properties
      bestOBB = {
        center: { x: centerX, y: centerY },
        width,
        height,
        rotation: Math.atan2(u.y, u.x), // Angle of the u-axis
      }
    }
  }

  //Return the minimum bounding box found
  return bestOBB
}

export default getMinimumBoundingBox